'use client';

import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Drawer, PulseIconButton } from 'src/components/PulseUI';
import { useGetStrategyTemplatesQuery } from 'src/api/redux/strategy';
import { useAiGenerator } from 'src/hooks/aiGenerator/useAiGenerator';
import { LOCATION } from 'src/hooks/filters/constants';
import {
  setDynamicCriteria,
  clearDynamicCriteria as clearDynamicCriteriaAction,
  updatePatientJourneyData,
  updateNotReferralsData,
  updateFreshFacesData,
} from 'src/redux';
import { CriteriaTemplate } from '../../types/criteriaTemplate';
import { TemplateCard } from '../TemplateCard';
import { transformTemplateWithStaticTypes } from '../../utils/placeholderUtils';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

interface TemplateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  strategyType: string;
  location: string;
}

const TemplateDrawer: React.FC<TemplateDrawerProps> = ({
  isOpen,
  onClose,
  strategyType,
  location,
}) => {
  const dispatch = useDispatch();
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | number | null
  >(null);

  const { clearDynamicCriteria } = useAiGenerator(location);

  const { data: templates = [], isLoading } = useGetStrategyTemplatesQuery(
    { type: strategyType },
    { skip: !strategyType || !isOpen }
  );

  const handleSelectTemplate = useCallback(
    (template: CriteriaTemplate) => {
      setSelectedTemplateId(template.id);

      // Handle different template structures based on type
      const config = template.config;
      const isPatientJourney = template.type === 'PATIENT_JOURNEY';

      if (!isPatientJourney) {
        // Regular template structure
        clearDynamicCriteria();
        setTimeout(() => {
          // Transform template - static types (this_provider, this_site) are auto-populated
          const { html, criteriaInputs } = transformTemplateWithStaticTypes(
            config.criteria_template,
            config.criteria_inputs
          );
          dispatch(
            setDynamicCriteria({
              criteria: {
                criteria_template: html,
                criteria_inputs: criteriaInputs,
              },
              location,
            })
          );
        }, 0);

        // Hydrate strategy-specific fields based on strategy type
        if (strategyType === 'NOT_REFERRING' && config.radius !== undefined) {
          dispatch(updateNotReferralsData({ radius: config.radius }));
        }

        if (strategyType === 'FRESH_FACES') {
          const freshFacesUpdates: Record<string, unknown> = {};
          if (config.code_set !== undefined) {
            freshFacesUpdates.code_set = config.code_set;
          }
          if (config.filter_set !== undefined) {
            freshFacesUpdates.filter_set = config.filter_set;
          }
          if (config.volume_type !== undefined) {
            freshFacesUpdates.volume_type = config.volume_type;
          }
          if (config.provider_type !== undefined) {
            freshFacesUpdates.provider_type = config.provider_type;
          }
          if (Object.keys(freshFacesUpdates).length > 0) {
            dispatch(updateFreshFacesData(freshFacesUpdates));
          }
        }
      } else {
        // PATIENT_JOURNEY template structure - populate both editors
        // Clear both locations first
        dispatch(
          clearDynamicCriteriaAction({
            location: LOCATION.PATIENT_JOURNEY_INDEX_EVENT,
          })
        );
        dispatch(
          clearDynamicCriteriaAction({
            location: LOCATION.PATIENT_JOURNEY_TARGET_EVENT,
          })
        );

        setTimeout(() => {
          // Populate index event editor
          if (config.index_event_template) {
            const { html: indexHtml, criteriaInputs: indexInputs } =
              transformTemplateWithStaticTypes(
                config.index_event_template.criteria_template,
                config.index_event_template.criteria_inputs
              );
            dispatch(
              setDynamicCriteria({
                criteria: {
                  criteria_template: indexHtml,
                  criteria_inputs: indexInputs,
                },
                location: LOCATION.PATIENT_JOURNEY_INDEX_EVENT,
              })
            );
          }

          // Populate target event editor
          if (config.target_event_template) {
            const { html: targetHtml, criteriaInputs: targetInputs } =
              transformTemplateWithStaticTypes(
                config.target_event_template.criteria_template,
                config.target_event_template.criteria_inputs
              );
            dispatch(
              setDynamicCriteria({
                criteria: {
                  criteria_template: targetHtml,
                  criteria_inputs: targetInputs,
                },
                location: LOCATION.PATIENT_JOURNEY_TARGET_EVENT,
              })
            );
          }
        }, 0);

        // Hydrate PATIENT_JOURNEY specific fields
        const patientJourneyUpdates: Record<string, unknown> = {};
        if (config.index_event_radius !== undefined) {
          patientJourneyUpdates.radius = config.index_event_radius;
        }
        if (config.target_event_interval !== undefined) {
          patientJourneyUpdates.target_event_interval =
            config.target_event_interval;
        }
        if (config.target_event_direction !== undefined) {
          patientJourneyUpdates.target_event_direction =
            config.target_event_direction;
        }
        if (Object.keys(patientJourneyUpdates).length > 0) {
          dispatch(updatePatientJourneyData(patientJourneyUpdates));
        }
      }
    },
    [clearDynamicCriteria, dispatch, location, strategyType]
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      anchor="right"
      data-testid="template-drawer"
      hideBackdrop
    >
      {/* Drawer Header */}
      <Drawer.Header
        className="bg-sky-100 text-primary border-b-0"
        data-testid="template-drawer-header"
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p
              className="font-bold text-lg"
              data-testid="template-drawer-title"
            >
              Templates
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PulseIconButton
              variant="plain"
              color="neutral"
              size="sm"
              icon={faXmark}
              data-testid="template-drawer-close-button"
              onClick={onClose}
              aria-label="Template Drawer"
              tooltip="Close Template Drawer"
            />
          </div>
        </div>
      </Drawer.Header>

      <Drawer.Content>
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-tertiary">
            Select a template to pre-fill the criteria editor. You can try
            different templates before closing.
          </p>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-pulse text-sm text-tertiary">
                Loading templates...
              </div>
            </div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-tertiary text-center py-8">
              No templates available.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplateId === template.id}
                  onClick={() => handleSelectTemplate(template)}
                />
              ))}
            </div>
          )}
        </div>
      </Drawer.Content>
    </Drawer>
  );
};

export default TemplateDrawer;
